import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority"

import { cn } from "../../lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-white transition-all duration-250 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 touch-target",
  {
    variants: {
      variant: {
        default: "bg-primary500 text-white hover:bg-primary600 hover:shadow-md active:bg-primary700",
        destructive:
          "bg-error200 text-white hover:bg-error300 hover:shadow-md active:scale-[0.98]",
        outline:
          "border border-secondary200 bg-white hover:bg-secondary100 hover:text-secondary900 hover:border-secondary300",
        secondary:
          "bg-secondary text-white hover:bg-secondary800 hover:shadow-md",
        ghost: "hover:bg-secondary100 hover:text-secondary900",
        link: "text-primary500 underline-offset-4 hover:underline",
        // Legacy variants for compatibility
        primary_sm:
          "bg-primary500 hover:bg-primary600 text-white py-2 px-4 w-fit flex gap-2 items-center",
        primary_full:
          "bg-primary500 hover:bg-primary600 text-white py-2 px-4 w-full flex gap-2 items-center justify-center",
        white_sm:
          "text-neutral700 py-0 px-4 w-fit flex gap-2 items-center border border-secondary200",
        white_full:
          "text-primary500 py-0 px-4 w-full flex gap-2 items-center border border-primary500 justify-center",
        tertiary:
          "bg-neutralGrey hover:bg-neutral200 text-neutral700 py-2 px-4 w-fit",
        danger:
          "bg-danger100 hover:bg-error200 text-white py-2 px-4 w-fit",
      },
      size: {
        default: "h-10 px-4 py-2 md:h-11 md:px-5",
        sm: "h-8 rounded-md px-3 text-xs md:h-9 md:text-sm",
        lg: "h-11 rounded-md px-6 md:h-12 md:px-8 text-base",
        icon: "h-10 w-10 md:h-11 md:w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }